import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import Button from '../components/ui/Button';
import { getFAQsForPage } from '../utils/faqContent';
import { generatePageFAQSchema } from '../utils/faqSchema';
import { socialLinks, getSocialIcon, usesReactIcon } from '../config/socialLinks';

const ContactPage = () => {
  // Get FAQs for contact page
  const contactFAQs = getFAQsForPage('contact');
  const faqSchema = generatePageFAQSchema('contact', 'https://mailvalidator.octalchip.com/contact', {
    pageTitle: 'Contact Us',
    pageDescription: 'Contact EmailValidator for email validation services, API integration, and technical support.'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Here you would typically send the form data to your backend
      console.log('Form submitted:', formData);
      
      setSubmitMessage('Thank you! Your message has been sent successfully. We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage('Sorry, there was an error sending your message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'MapPin',
      title: 'Head Office',
      details: [
        'Octalchip',
        '404, Opera point, kothariya,',
        'gujarat, india, 360022'
      ],
      link: 'https://maps.google.com/?q=404+Opera+point+kothariya+gujarat+india+360022'
    },
    {
      icon: 'Mail',
      title: 'Email',
      details: ['business@octalchip.com'],
      link: 'mailto:business@octalchip.com'
    },
    {
      icon: 'Phone',
      title: 'Phone',
      details: ['+91 7574082582'],
      link: 'tel:+917574082582'
    },
    {
      icon: 'Clock',
      title: 'Working Hours',
      details: ['Mon - Fri, 9AM - 6PM'],
      link: null
    }
  ];

  // Filter out Email, WhatsApp, and YouTube from social links display
  const displaySocialLinks = socialLinks.filter(
    link => link.name !== 'Email' && link.name !== 'WhatsApp' && link.name !== 'YouTube'
  );

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <SEO 
        title="Contact Us"
        description="Contact EmailValidator for email validation services, API integration, and technical support. Get a free consultation and quote for your email validation needs."
        keywords="contact email validator, email validation support, technical support, email validation consultation, customer service, contact form, email validation help"
        canonical="https://mailvalidator.octalchip.com/contact"
        structuredData={faqSchema}
      />
      
      <Header />
        {/* Hero Section */}
        <section id="contact-hero" className="bg-white py-20">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-5xl font-bold text-gray-800 mb-6">
                Get In Touch
              </h1>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-8"></div>
              <p className="text-xl text-gray-600 leading-relaxed">
                Ready to improve your email validation? We'd love to hear from you. 
                Let's discuss how we can help optimize your email marketing results.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information Section */}
        <section id="contact-information" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Contact Information</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactInfo.map((info, index) => (
                <div key={index} className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={info.icon} size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{info.title}</h3>
                  {info.link ? (
                    <a 
                      href={info.link} 
                      target={info.link.startsWith('http') ? '_blank' : '_self'}
                      rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                      className="text-gray-600 hover:text-[#00d690] transition-colors"
                    >
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="mb-1">{detail}</p>
                      ))}
                    </a>
                  ) : (
                    info.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600 mb-1">{detail}</p>
                    ))
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">Send Us a Message</h2>
                <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-12">
                {/* Contact Form */}
                <div>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                          placeholder="Enter your phone number"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                          Subject *
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                          placeholder="What is this about?"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d690]/20 focus:border-[#00d690]"
                        placeholder="Tell us about your email validation needs..."
                      ></textarea>
                    </div>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button
                      type="submit"
                      variant="success"
                      size="md"
                      fullWidth
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      animation="pulse"
                      className="bg-[#00d690] hover:bg-[#00b876]"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </div>
                
                {/* Quick Contact */}
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Quick Contact</h3>
                    <div className="space-y-4">
                      <a
                        href="https://wa.me/917574082582?text=Hi! I'm interested in your email validation services. Can you help me get started?"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <Icon name="MessageCircle" size={24} className="text-green-600" />
                        <div>
                          <div className="font-semibold text-green-800">WhatsApp Chat</div>
                          <div className="text-sm text-green-600">Get instant response</div>
                        </div>
                      </a>
                      
                      <a
                        href="mailto:business@octalchip.com?subject=Email Validation Services Inquiry&body=Hi Octalchip team,%0D%0A%0D%0AI'm interested in your email validation services. Could you please provide more information about your pricing and features?%0D%0A%0D%0ABest regards,%0D%0A[Your Name]"
                        className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Icon name="Mail" size={24} className="text-blue-600" />
                        <div>
                          <div className="font-semibold text-blue-800">Email Us</div>
                          <div className="text-sm text-blue-600">Direct email contact</div>
                        </div>
                      </a>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Follow Us</h3>
                    <div className="flex flex-wrap gap-2 sm:gap-3 justify-center sm:justify-start">
                      {displaySocialLinks.map((social, index) => {
                        const ReactIcon = getSocialIcon(social);
                        // Prioritize react-icon if available, otherwise use Lucide icon, fallback to text
                        const renderIcon = () => {
                          if (ReactIcon) {
                            return <ReactIcon className="w-5 h-5 sm:w-6 sm:h-6" />;
                          }
                          if (social.icon) {
                            return <Icon name={social.icon} className="w-5 h-5 sm:w-6 sm:h-6" />;
                          }
                          // Fallback: show first letter of platform name
                          return <span className="text-xs sm:text-sm font-semibold">{social.name.charAt(0)}</span>;
                        };
                        
                        return (
                          <a
                            key={index}
                            href={social.url}
                            target={social.url.startsWith('mailto:') ? '_self' : '_blank'}
                            rel={social.url.startsWith('mailto:') ? '' : 'noopener noreferrer'}
                            className="w-11 h-11 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center hover:bg-[#00d690] hover:text-white transition-all duration-200 ease-in-out active:scale-95 touch-manipulation shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#00d690]/30"
                            title={social.name}
                            aria-label={social.name}
                          >
                            {renderIcon()}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="text-lg font-bold text-gray-800 mb-4">Response Time</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>Email:</span>
                        <span className="font-medium">Within 24 hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span>WhatsApp:</span>
                        <span className="font-medium">Within 2 hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Phone:</span>
                        <span className="font-medium">Immediate</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section id="faq" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-gray-600">
                  Common questions about contacting us and getting support
                </p>
              </div>
              <FAQ items={contactFAQs} variant="default" />
            </div>
          </div>
        </section>
        
        <Footer />
    </div>
  );
};

export default ContactPage; 